import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { parseISO, format } from 'date-fns';
import { enUS, uk } from 'date-fns/locale';
import { RecordType } from '@vehicle-logbook/shared';
import { Download, PieChart, TrendingUp, Car } from 'lucide-react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const CHART_COLORS = [
  '#0284c7',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#64748b',
];

interface ExpensesByCategoryRow {
  category: RecordType;
  total: number;
  count: number;
}

interface ExpensesByMonthRow {
  period: string;
  total: number;
}

interface ExpensesByVehicleRow {
  vehicleId: string;
  vehicleName: string;
  total: number;
  count: number;
}

interface AnalyticsExpensesData {
  totalExpenses: number;
  totalRecords: number;
  expensesByCategory: ExpensesByCategoryRow[];
  expensesByMonth: ExpensesByMonthRow[];
  expensesByVehicle: ExpensesByVehicleRow[];
}

export const Analytics = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const dateLocale = i18n.language?.startsWith('uk') ? uk : enUS;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['analytics-expenses'],
    queryFn: async () => {
      if (!user) return null;
      const token = await user.getIdToken();
      const res = await axios.get<{ data: AnalyticsExpensesData }>(
        `${API_URL}/analytics/expenses`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data.data;
    },
    enabled: !!user,
  });

  const formatMoney = (amount: number) => {
    const lang = i18n.language?.startsWith('uk') ? 'uk-UA' : 'en-US';
    return new Intl.NumberFormat(lang, {
      style: 'currency',
      currency: 'UAH',
    }).format(amount);
  };

  const handleExportCsv = async () => {
    if (!user) return;
    const token = await user.getIdToken();
    const response = await axios.get(`${API_URL}/analytics/export`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { format: 'csv' },
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'vehicle-logbook-export.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const categoryChartData =
    data && data.expensesByCategory.length > 0
      ? {
          labels: data.expensesByCategory.map((row) =>
            t(`record.types.${row.category}`)
          ),
          datasets: [
            {
              data: data.expensesByCategory.map((row) => row.total),
              backgroundColor: data.expensesByCategory.map(
                (_, i) => CHART_COLORS[i % CHART_COLORS.length]
              ),
              borderWidth: 0,
            },
          ],
        }
      : null;

  const monthChartData =
    data && data.expensesByMonth.length > 0
      ? {
          labels: data.expensesByMonth.map((row) => {
            try {
              return format(parseISO(`${row.period}-01`), 'LLL yyyy', {
                locale: dateLocale,
              });
            } catch {
              return row.period;
            }
          }),
          datasets: [
            {
              label: t('analytics.totalExpenses'),
              data: data.expensesByMonth.map((row) => row.total),
              backgroundColor: 'rgba(2, 132, 199, 0.35)',
              borderColor: '#0284c7',
              borderWidth: 2,
              borderRadius: 6,
            },
          ],
        }
      : null;

  const vehicleChartData =
    data && data.expensesByVehicle.length > 0
      ? {
          labels: data.expensesByVehicle.map((v) => v.vehicleName),
          datasets: [
            {
              label: t('analytics.totalExpenses'),
              data: data.expensesByVehicle.map((v) => v.total),
              backgroundColor: 'rgba(16, 185, 129, 0.45)',
              borderColor: '#10b981',
              borderWidth: 2,
              borderRadius: 6,
            },
          ],
        }
      : null;

  const axisTickColor = '#64748b';
  const gridColor = 'rgba(100, 116, 139, 0.12)';

  const moneyTooltipLabel = (item: {
    parsed: { x?: number | null; y?: number | null };
  }) => {
    const v = item.parsed.y ?? item.parsed.x ?? 0;
    return formatMoney(v);
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false as const,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (item: { parsed: { x?: number | null; y?: number | null } }) =>
            moneyTooltipLabel(item),
        },
      },
    },
    scales: {
      x: {
        ticks: { color: axisTickColor, maxRotation: 45, minRotation: 0 },
        grid: { color: gridColor },
      },
      y: {
        ticks: {
          color: axisTickColor,
          callback: (v: string | number) =>
            formatMoney(Number(v)).replace(/\s/g, ' '),
        },
        grid: { color: gridColor },
      },
    },
  };

  const vehicleBarOptions = {
    ...barOptions,
    indexAxis: 'y' as const,
    scales: {
      x: {
        ticks: {
          color: axisTickColor,
          callback: (v: string | number) =>
            formatMoney(Number(v)).replace(/\s/g, ' '),
        },
        grid: { color: gridColor },
      },
      y: {
        ticks: { color: axisTickColor },
        grid: { display: false },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false as const,
    cutout: '58%',
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          color: axisTickColor,
          boxWidth: 10,
          padding: 14,
          usePointStyle: true,
        },
      },
      tooltip: {
        callbacks: {
          label: (item: { label: string; parsed: number | null }) =>
            `${item.label}: ${formatMoney(item.parsed ?? 0)}`,
        },
      },
    },
  };

  if (!user) {
    return null;
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t('analytics.title')}
          </h1>
          <p className="mt-1 text-sm text-gray-600">{user.email}</p>
        </div>
        <button
          type="button"
          onClick={() => void handleExportCsv()}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
        >
          <Download className="h-4 w-4 text-primary-600" />
          {t('analytics.exportData')}
        </button>
      </div>

      {isLoading && (
        <div className="flex min-h-[240px] items-center justify-center rounded-xl border border-gray-100 bg-white shadow-sm">
          <p className="text-gray-500">{t('common.loading')}</p>
        </div>
      )}

      {isError && (
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800">
          {t('errors.generic')}
        </div>
      )}

      {!isLoading && !isError && data && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary-100 p-2.5">
                  <TrendingUp className="h-5 w-5 text-primary-700" />
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {t('analytics.totalExpenses')}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatMoney(data.totalExpenses)}
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-emerald-100 p-2.5">
                  <PieChart className="h-5 w-5 text-emerald-700" />
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {t('analytics.totalRecords')}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {data.totalRecords}
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-amber-100 p-2.5">
                  <Car className="h-5 w-5 text-amber-700" />
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {t('analytics.vehiclesWithData')}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {data.expensesByVehicle.length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {data.totalRecords === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 px-6 py-16 text-center">
              <p className="text-gray-600">{t('analytics.emptyState')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
              {categoryChartData && (
                <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm lg:col-span-5">
                  <h2 className="mb-4 text-lg font-semibold text-gray-900">
                    {t('analytics.byCategory')}
                  </h2>
                  <div className="h-72">
                    <Doughnut
                      data={categoryChartData}
                      options={doughnutOptions}
                    />
                  </div>
                </div>
              )}
              {monthChartData && (
                <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm lg:col-span-7">
                  <h2 className="mb-4 text-lg font-semibold text-gray-900">
                    {t('analytics.monthlyTrend')}
                  </h2>
                  <div className="h-80">
                    <Bar data={monthChartData} options={barOptions} />
                  </div>
                </div>
              )}
              {vehicleChartData && data.expensesByVehicle.length > 1 && (
                <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm lg:col-span-12">
                  <h2 className="mb-4 text-lg font-semibold text-gray-900">
                    {t('analytics.byVehicle')}
                  </h2>
                  <div className="h-72 max-w-3xl">
                    <Bar data={vehicleChartData} options={vehicleBarOptions} />
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
